import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Package, Pencil, CheckCircle2, XCircle, Users, TrendingUp, List, X,
} from 'lucide-react'
import { licensingApi } from '@/api/licensing'
import type { OrgSystemSubscription, System } from '@/types'
import { Loading, EmptyState } from '@/components/ui'
import { cn } from '@/utils/helpers'
import { formatNIS } from '@/utils/currency'

// ─── System badge colours ─────────────────────────────────────────────────────

const SYSTEM_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  dugri:        { bg: '#7c3aed20', text: '#c4b5fd', border: '#7c3aed50' },
  spaceviz:     { bg: '#2563eb20', text: '#93c5fd', border: '#2563eb50' },
  jumpstart:    { bg: '#16a34a20', text: '#4ade80', border: '#16a34a50' },
  milonyiftach: { bg: '#f59e0b20', text: '#fbbf24', border: '#f59e0b50' },
  clientportal: { bg: '#8b5cf620', text: '#d8b4fe', border: '#8b5cf650' },
  whatsapp:     { bg: '#10b98120', text: '#6ee7b7', border: '#10b98150' },
  gesher:       { bg: '#f43f5e20', text: '#fda4af', border: '#f43f5e50' },
}

const getSystemColor = (code: string) =>
  SYSTEM_COLORS[code.toLowerCase()] ?? { bg: '#6b728020', text: '#9ca3af', border: '#6b728040' }

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active:    { bg: '#22c55e20', text: '#4ade80' },
  trial:     { bg: '#f59e0b20', text: '#fbbf24' },
  suspended: { bg: '#ef444420', text: '#f87171' },
  cancelled: { bg: '#6b728020', text: '#9ca3af' },
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatChip({ label, value, variant = 'default' }: {
  label: string; value: string | number; variant?: 'default' | 'success' | 'warning'
}) {
  return (
    <div className={cn(
      'flex flex-col items-center px-4 py-2 rounded-lg border',
      variant === 'success' ? 'bg-teal/10 border-teal/30' :
      variant === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
      'bg-navy-card border-navy-border'
    )}>
      <span className={cn('text-lg font-bold num',
        variant === 'success' ? 'text-teal' :
        variant === 'warning' ? 'text-amber-400' :
        'text-ink-primary'
      )}>{value}</span>
      <span className="text-[11px] text-ink-muted mt-0.5">{label}</span>
    </div>
  )
}

// ─── Subscriptions table ──────────────────────────────────────────────────────

type SubWithOrg = OrgSystemSubscription & { organizations: { name: string } | null }

interface SubsTableProps {
  subs: SubWithOrg[]
  systems: System[]
  filter: string | null
  onFilter: (code: string | null) => void
  onEditOrg: (orgId: string) => void
}

function SubsTable({ subs, systems, filter, onFilter, onEditOrg }: SubsTableProps) {
  const filtered = filter
    ? subs.filter(s => s.system_code.toLowerCase() === filter.toLowerCase())
    : subs

  return (
    <div>
      {/* Section header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <List className="h-5 w-5 text-indigo-400" />
          <h2 className="text-lg font-semibold text-ink-primary">
            Subscriptions
            <span className="text-ink-muted text-sm font-normal ml-2">({filtered.length})</span>
          </h2>
        </div>

        {/* System filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onFilter(null)}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
              !filter
                ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50'
                : 'text-ink-muted border-navy-border hover:border-indigo-500/30 hover:text-indigo-300'
            )}
          >
            All
          </button>
          {systems.map(sys => {
            const c = getSystemColor(sys.code)
            const active = filter?.toLowerCase() === sys.code.toLowerCase()
            return (
              <button
                key={sys.code}
                onClick={() => onFilter(active ? null : sys.code)}
                className="px-3 py-1 rounded-full text-xs font-medium border transition-colors"
                style={{
                  background: active ? c.bg : 'transparent',
                  color: active ? c.text : '#6b7280',
                  borderColor: active ? c.border : '#2a2d3e',
                }}
              >
                {sys.code.toUpperCase()}
              </button>
            )
          })}
          {filter && (
            <button
              onClick={() => onFilter(null)}
              className="p-1 rounded text-ink-muted hover:text-ink-primary transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted text-sm">No subscriptions found</div>
      ) : (
        <div className="card overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[140px_1fr_120px_100px_110px_100px_1fr_48px] gap-3 px-4 py-2.5 border-b border-navy-border text-[11px] text-ink-muted uppercase tracking-wider font-medium">
            <span>System</span>
            <span>Organization</span>
            <span>Plan</span>
            <span>Status</span>
            <span>Billing</span>
            <span>Price</span>
            <span>Notes</span>
            <span />
          </div>

          <div className="divide-y divide-navy-border">
            {filtered.map(sub => {
              const sc_color = getSystemColor(sub.system_code)
              const st_color = STATUS_COLORS[sub.status] ?? STATUS_COLORS.cancelled
              const orgName = sub.organizations?.name ?? null

              return (
                <div
                  key={sub.id}
                  className="grid grid-cols-1 md:grid-cols-[140px_1fr_120px_100px_110px_100px_1fr_48px] gap-2 md:gap-3 px-4 py-3 hover:bg-navy-hover/40 transition-colors items-center"
                >
                  {/* System badge */}
                  <div>
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide"
                      style={{ background: sc_color.bg, color: sc_color.text, border: `1px solid ${sc_color.border}` }}
                    >
                      {sub.system_code}
                    </span>
                  </div>

                  {/* Org name */}
                  <div className="min-w-0">
                    <span className="text-sm text-ink-primary truncate block">
                      {orgName ?? <span className="text-ink-muted italic">Unknown org</span>}
                    </span>
                  </div>

                  {/* Plan code */}
                  <div>
                    <span className="text-xs text-ink-secondary font-mono">{sub.plan_code}</span>
                  </div>

                  {/* Status */}
                  <div>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                      style={{ background: st_color.bg, color: st_color.text }}
                    >
                      {sub.status}
                    </span>
                  </div>

                  {/* Billing cycle */}
                  <div>
                    <span className="text-xs text-ink-muted capitalize">{sub.billing_cycle}</span>
                  </div>

                  {/* Price */}
                  <div>
                    <span className="text-xs text-amber-400 font-mono num">
                      {sub.price_agorot > 0 ? formatNIS(sub.price_agorot) : '—'}
                    </span>
                  </div>

                  {/* Notes */}
                  <div className="min-w-0">
                    {sub.notes ? (
                      <span className="text-xs text-ink-muted truncate block" title={sub.notes}>
                        {sub.notes}
                      </span>
                    ) : (
                      <span className="text-xs text-navy-border">—</span>
                    )}
                  </div>

                  {/* Go to org */}
                  <div className="flex justify-end">
                    {sub.organization_id && (
                      <button
                        onClick={() => onEditOrg(sub.organization_id)}
                        title="Go to org"
                        className="p-1.5 rounded-lg text-ink-muted hover:text-indigo-400 hover:bg-indigo-500/10 transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ProductsPage() {
  const navigate = useNavigate()
  const [systemFilter, setSystemFilter] = useState<string | null>(null)

  const { data: systems = [], isLoading } = useQuery({
    queryKey: ['admin-products'],
    queryFn: licensingApi.listSystems,
    staleTime: 60_000,
  })

  const { data: systemStats = {} } = useQuery({
    queryKey: ['admin-products-stats'],
    queryFn: licensingApi.getSystemStats,
    staleTime: 60_000,
  })

  const { data: allSubs = [], isLoading: subsLoading } = useQuery({
    queryKey: ['admin-all-subscriptions'],
    queryFn: () => licensingApi.listSubscriptions(),
    staleTime: 60_000,
  })

  const { data: allPlans = [] } = useQuery({
    queryKey: ['admin-all-plans'],
    queryFn: () => licensingApi.listAllPlans(false),
    staleTime: 60_000,
  })

  const planCountBySystem = allPlans.reduce<Record<string, number>>((acc, p) => {
    acc[p.system_code] = (acc[p.system_code] ?? 0) + 1
    return acc
  }, {})

  const totalActiveSystems = systems.filter(s => s.is_active).length
  const totalActiveSubs = Object.values(systemStats).reduce((s, v) => s + v.active_subs, 0)
  const totalRevAgorot = Object.values(systemStats).reduce((s, v) => s + v.total_revenue, 0)

  const handleShowSubs = (code: string) => {
    setSystemFilter(code)
    setTimeout(() => {
      document.getElementById('subs-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-ink-primary">Products & Subscriptions</h1>
          <p className="text-sm text-ink-secondary mt-1">
            {systems.length} systems · {totalActiveSystems} active
          </p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="flex flex-wrap gap-3 mb-8">
        <StatChip label="Active systems"  value={totalActiveSystems}        variant="success" />
        <StatChip label="Active subs"     value={totalActiveSubs}           variant="success" />
        <StatChip label="Monthly revenue" value={formatNIS(totalRevAgorot)} variant="warning" />
        <StatChip label="Total systems"   value={systems.length} />
      </div>

      {/* System cards */}
      {isLoading ? (
        <Loading text="Loading systems..." />
      ) : !systems.length ? (
        <EmptyState
          icon={<Package className="h-10 w-10" />}
          title="No systems found"
          description="No systems are configured in the database"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-10">
          {systems.map(sys => {
            const stats = systemStats[sys.code]
            const planCount = planCountBySystem[sys.code] ?? 0
            const color = getSystemColor(sys.code)
            return (
              <div
                key={sys.id}
                className={cn(
                  'card p-5 flex flex-col gap-4 transition-all duration-200',
                  !sys.is_active && 'opacity-60'
                )}
              >
                {/* Card header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: color.bg, border: `1px solid ${color.border}` }}
                    >
                      <span className="text-base font-bold" style={{ color: color.text }}>
                        {sys.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-ink-primary text-sm leading-tight">{sys.name}</h3>
                      <span className="text-[11px] text-ink-muted font-mono">{sys.code}</span>
                    </div>
                  </div>

                  {sys.is_active ? (
                    <span className="flex items-center gap-1 text-[11px] text-teal font-medium flex-shrink-0">
                      <CheckCircle2 className="h-3.5 w-3.5" />Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[11px] text-ink-muted font-medium flex-shrink-0">
                      <XCircle className="h-3.5 w-3.5" />Inactive
                    </span>
                  )}
                </div>

                {/* Description */}
                {sys.description && (
                  <p className="text-xs text-ink-secondary leading-relaxed line-clamp-2">{sys.description}</p>
                )}

                {/* Stats row */}
                <div className="flex items-center gap-4 py-3 border-y border-navy-border">
                  <div className="flex items-center gap-1.5 text-xs text-ink-secondary">
                    <Users className="h-3.5 w-3.5 text-teal" />
                    <span className="font-semibold text-ink-primary num">{stats?.active_subs ?? 0}</span>
                    <span>subscribers</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-ink-secondary">
                    <Package className="h-3.5 w-3.5 text-indigo-400" />
                    <span className="font-semibold text-ink-primary num">{planCount}</span>
                    <span>plans</span>
                  </div>
                  {(stats?.total_revenue ?? 0) > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-ink-secondary ms-auto">
                      <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
                      <span className="num text-amber-400 font-semibold">{formatNIS(stats!.total_revenue)}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center">
                  <button
                    onClick={() => handleShowSubs(sys.code)}
                    className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-indigo-400 transition-colors"
                  >
                    <List className="h-3.5 w-3.5" />Show subscriptions
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Subscriptions table */}
      <div id="subs-table">
        {subsLoading ? (
          <Loading text="Loading subscriptions..." />
        ) : (
          <SubsTable
            subs={allSubs}
            systems={systems}
            filter={systemFilter}
            onFilter={setSystemFilter}
            onEditOrg={(orgId) => navigate(`/organizations/${orgId}`)}
          />
        )}
      </div>
    </div>
  )
}
